/**
 * 仕込み(動画外の前工程)の作り方をレシピ文書から取り込む(#250)。
 *
 * 仕込みの動画が無くても、作り方がレシピ(PDF)に書いてあれば撮影を待たずにマニュアルへ載せる。
 * 文書は品目の「レシピ・メモ」から選ぶか、ここで新しく上げる。上げた文書は品目の素材置き場に
 * 新しい名前で置く(既存の素材を上書きしない。uploadMaterial と同じ規則)。
 *
 * 画面は文書を指すだけで、抜き出しはワーカー(CLI の prep-doc)が行う。文書に作り方が
 * 見当たらなければジョブは失敗し、理由がここに出る(似た別物で埋めない)。
 *
 * **失敗したら次の手を選ばせる。** 文書に作り方が無い失敗は、同じ文書で取り込み直しても直らない。
 * 理由を読める言葉で出し、「作り方を入力する / 別の資料から取り込む / 市販品にする /
 * 撮影して仕込みSOPを作る」を並べる(揚げマナガツオの「揚げにんにく」は、レシピにもメモにも
 * 作り方が無く、画面に取り込み直ししか無いため行き止まりになった)。
 *
 * **アラートと操作を離さない。** 失敗は品目画面・レビュー画面の上部にアラート(alert)として出し、
 * 理由・選択肢・入力欄・「回答を本文に反映」まで同じ箇所で完結させる(別の画面の奥にある
 * 仕込み一覧まで辿らせない。2026-09-30 柴田の指示)。
 */
import { useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebase";
import { useI18n } from "../lib/i18n";
import { editDocId, parsePrepProcedure, type EditDoc, type PrepProcedure } from "../lib/manual-edits";
import { parseMaterialPath, previewKind } from "../lib/materials";
import { prepFailureOf } from "../lib/prep-failure";
import type { FailureKind, JobType } from "../lib/types";
import { mb, uploadMaterial, type UploadProgress } from "../lib/upload";
import { PrepProcedureEditPanel } from "./PrepProcedureEditPanel";

const functions = getFunctions(app, "asia-northeast1");
const importPrepDoc = httpsCallable<
  { dishId: string; ingredient: string; docPath: string },
  { jobId: string }
>(functions, "importPrepDoc");
/** 仕込みの扱いの切り替え(#18)。品目詳細の紐づけと同じ Callable */
const linkPrep = httpsCallable<
  { dishId: string; ingredient: string; external?: boolean; reset?: boolean; note?: string },
  { jobId: string }
>(functions, "linkPrep");
/** 入力した作り方を本文へ入れる。品目画面の「回答を本文に反映」と同じ Callable */
const applyAnswers = httpsCallable<{ dishId: string; approve: boolean }, unknown>(functions, "applyAnswers");

/**
 * 扱いを選んだときに仕込みへ残す補足。質問への回答(src/commands/review.ts の prerequisite)と
 * 同じ文言にする(SOP・一覧で同じ意味の補足が2通りの書き方で並ばないように)
 */
const EXTERNAL_NOTE = "市販品・外部調達(料理長確認)";
const FILM_NOTE = "自家製 — 仕込みSOPの作成(撮影)が必要(料理長確認)";

const EMPTY_PROCEDURE: PrepProcedure = { ingredients: [], steps: [], notes: [] };

/** 取り込みに使える文書(Gemini に渡せる形)。動画など他の素材は出さない */
export function importableDocs(paths: string[]): string[] {
  return paths.filter((p) => {
    const k = previewKind(p);
    return k === "pdf" || k === "image";
  });
}

export interface PrepJob {
  /** 取り込み(prep-doc)か扱いの切り替え(link-prep)か。失敗の案内は取り込みにだけ出す */
  type: JobType;
  status: "queued" | "running" | "succeeded" | "failed";
  error: string | null;
  errorKind?: FailureKind | null;
  /** 取り込みに使った文書(GCS のキー) */
  docPath?: string | null;
}

interface Props {
  dishId: string;
  /** 仕込みの名前(本文の prerequisites[].name) */
  name: string;
  /** 品目のレシピ・メモ(recipeDocPaths) */
  recipeDocs: string[];
  /** この仕込みの直近の取り込みジョブ */
  job: PrepJob | null;
  /** 品目で他のジョブが動いている(二重起動は Functions が拒むので、先に押せなくする) */
  busy: boolean;
  /** 既に取り込み済み(取り込み直し) */
  documented?: boolean;
  /** 記録済みのマニュアル編集(manualEdits 全件)。作り方を入力した後の「反映待ち」を出すため */
  edits: Record<string, EditDoc>;
  /**
   * 画面上部のアラートとして出す。見出しに仕込みの名前を出し、失敗も入力済みも無ければ何も出さない
   * (取り込みの入口は仕込み一覧に残す)
   */
  alert?: boolean;
  /** 失敗の案内を出さない(同じ失敗を上部のアラートが出しているとき、一覧で重ねない) */
  hideFailure?: boolean;
}

type Confirm = "external" | "film" | null;

export function PrepDocImport({ dishId, name, recipeDocs, job, busy, documented, edits, alert, hideFailure }: Props) {
  const { t } = useI18n();
  const docs = importableDocs(recipeDocs);
  const [open, setOpen] = useState(false);
  const [writing, setWriting] = useState(false);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [choice, setChoice] = useState<string>(docs[0] ?? "");
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const running = job?.status === "queued" || job?.status === "running";
  // 作り方を入力済み(反映待ち)。取り込み済みの仕込みの手直しは Review 側が出すので、未作成のときだけ
  const written = !documented && edits[editDocId("prerequisite", name, "procedure")]
    ? parsePrepProcedure(edits[editDocId("prerequisite", name, "procedure")].value)
    : null;
  const failure =
    job?.type === "prep-doc" && job.status === "failed" && !written && !hideFailure ? prepFailureOf(job) : null;

  async function apply() {
    setStarting(true);
    setError(null);
    try {
      await applyAnswers({ dishId, approve: false });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setStarting(false);
    }
  }

  async function switchTo(kind: "external" | "film") {
    setStarting(true);
    setError(null);
    try {
      await linkPrep(
        kind === "external"
          ? { dishId, ingredient: name, external: true, note: EXTERNAL_NOTE }
          : { dishId, ingredient: name, reset: true, note: FILM_NOTE },
      );
      setConfirm(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setStarting(false);
    }
  }

  async function start() {
    setStarting(true);
    setError(null);
    try {
      const docPath = file
        ? await uploadMaterial(dishId, file, "doc", (_p, p) => setProgress(p))
        : choice;
      if (!docPath) throw new Error(t("レシピを選ぶか、PDFを選んでください"));
      await importPrepDoc({ dishId, ingredient: name, docPath });
      setOpen(false);
      setFile(null);
      setProgress(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setStarting(false);
    }
  }

  // アラートとして出すときは、上部の注意枠に入れる(品目画面・レビュー画面の見出しの直下)
  const box = alert ? "prep-import warn prep-alert" : "prep-import";

  if (running) {
    return (
      <p className={`${box} muted small`}>
        {job?.type === "link-prep"
          ? t("「{name}」の扱いを切り替えています（数分かかります）。終わるとこの画面に反映されます。", { name })
          : t("レシピから作り方を取り込んでいます（数分かかります）。終わるとこの画面に反映されます。")}
      </p>
    );
  }

  if (writing) {
    return (
      <div className={box}>
        <PrepProcedureEditPanel
          dishId={dishId}
          name={name}
          current={EMPTY_PROCEDURE}
          edits={edits}
          creating
          onClose={() => setWriting(false)}
        />
      </div>
    );
  }

  // アラートは「失敗している」か「入力した作り方が反映待ち」のときだけ出す。取り込みの入口は仕込み一覧にある
  if (alert && !failure && !written && !open) return null;

  const docName = job?.docPath ? parseMaterialPath(job.docPath).fileName : null;

  return (
    <div className={box}>
      {written && (
        <div className="prep-proc small">
          <span className="badge p-medium">{t("入力した作り方（反映待ち）")}</span>
          <ol>
            {written.steps.map((st, i) => (
              <li key={i}>{st}</li>
            ))}
          </ol>
          <p className="muted">{t("「回答を本文に反映」でマニュアルに入ります。")}</p>
          <div className="prep-import-row">
            <button disabled={busy || starting} onClick={() => void apply()}>
              {starting ? t("起動中…") : t("回答を本文に反映")}
            </button>
            <button className="link" disabled={busy || starting} onClick={() => setWriting(true)}>
              {t("入力した作り方を直す")}
            </button>
          </div>
        </div>
      )}
      {failure && (
        <div className="prep-failure">
          {failure.notFound ? (
            <>
              <p className="error small">
                <strong>
                  {docName
                    ? t("「{name}」の作り方は、選んだ資料「{doc}」に書かれていませんでした。", { name, doc: docName })
                    : t("「{name}」の作り方は、選んだ資料に書かれていませんでした。", { name })}
                </strong>
              </p>
              {failure.detail && (
                <p className="muted small">
                  {t("資料を読んだ結果:")} {failure.detail}
                </p>
              )}
              <p className="small">{t("同じ資料で取り込み直しても結果は変わりません。次のどれかを選んでください。")}</p>
            </>
          ) : (
            <>
              <p className="error small">
                <strong>{t("「{name}」の取り込みが途中で止まりました（システム側のエラー）。", { name })}</strong>
              </p>
              {failure.detail && <p className="muted small">{t("エラーの内容:")} {failure.detail}</p>}
              <p className="small">{t("もう一度取り込むか、次のどれかを選んでください。")}</p>
            </>
          )}
          <div className="q-options prep-choices">
            <button className="chip" disabled={busy || starting} onClick={() => setWriting(true)}>
              {t("作り方を入力する（料理長が書く）")}
            </button>
            <button className="chip" disabled={busy || starting} onClick={() => setOpen(true)}>
              {failure.notFound ? t("別の資料（PDF・写真）から取り込む") : t("資料を選んでもう一度取り込む")}
            </button>
            <button className="chip" disabled={busy || starting} onClick={() => setConfirm("external")}>
              {t("市販品・外部調達にする（仕込みSOPは不要）")}
            </button>
            <button className="chip" disabled={busy || starting} onClick={() => setConfirm("film")}>
              {t("自家製として、仕込みを撮影してSOPを作る")}
            </button>
          </div>
          {confirm && (
            <div className="prep-import-row">
              <span className="small">
                {confirm === "external"
                  ? t("「{name}」を市販品・外部調達として記録します。マニュアルは数分で作り直されます。", { name })
                  : t("「{name}」を自家製（仕込みの撮影が必要）として記録します。マニュアルは数分で作り直されます。", { name })}
              </span>
              <button disabled={starting || busy} onClick={() => void switchTo(confirm)}>
                {starting ? t("起動中…") : t("記録する")}
              </button>
              <button className="link" disabled={starting} onClick={() => setConfirm(null)}>
                {t("やめる")}
              </button>
            </div>
          )}
        </div>
      )}
      {!open ? (
        !failure && !written && (
          <button className="link" disabled={busy} onClick={() => setOpen(true)}>
            {documented ? t("別のレシピから取り込み直す") : t("動画が無い場合: レシピ（PDF）から作り方を取り込む")}
          </button>
        )
      ) : (
        <div className="prep-import-panel">
          <p className="muted small">
            {t("選んだレシピから「{name}」の材料・分量・手順だけを抜き出し、マニュアルの「仕込み」に載せます。レシピに書いていないことは補いません。", { name })}
          </p>
          {docs.length > 0 && (
            <label className="prep-import-row">
              {t("品目のレシピ・メモから選ぶ")}
              <select
                value={file ? "" : choice}
                disabled={!!file}
                onChange={(e) => setChoice(e.target.value)}
              >
                {docs.map((p) => (
                  <option key={p} value={p}>
                    {parseMaterialPath(p).fileName}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="prep-import-row">
            {docs.length > 0 ? t("または、PDFを新しく上げる") : t("レシピのPDFを選ぶ")}
            <input
              type="file"
              accept="application/pdf,image/*"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </label>
          {progress && !progress.done && (
            <p className="muted small">
              {t("アップロード中")} {mb(progress.transferred)} / {mb(progress.total)}
            </p>
          )}
          <div className="prep-import-row">
            <button disabled={starting || busy || (!file && !choice)} onClick={() => void start()}>
              {starting ? t("起動中…") : t("取り込む")}
            </button>
            <button className="link" disabled={starting} onClick={() => setOpen(false)}>
              {t("やめる")}
            </button>
          </div>
        </div>
      )}
      {error && <p className="error small">{error}</p>}
    </div>
  );
}
